import { Linkedin, Mail } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Shared";

const RESULT_STATS = [
  { value: "10+", label: "Meetings booked" },
  { value: "35%", label: "Positive response rate" },
  { value: "4", label: "Weeks to results" },
];

const OUTREACH_STATS = [
  { value: "48", label: "Decision-makers in sequence" },
  { value: "5", label: "Touchpoints per prospect" },
  { value: "7", label: "Replies received" },
  { value: "3", label: "Qualified conversations" },
];

const LEAD_STATUSES = [
  { label: "Contacted", count: 3, cls: "bg-violet-700 text-white" },
  { label: "Follow up later", count: 1, cls: "bg-violet-100 text-violet-800" },
  { label: "Not the right person", count: 1, cls: "bg-[#E4E0EE] text-[#5B21B6]" },
  { label: "Not interested", count: 2, cls: "bg-[#3B0764] text-white" },
];

const EMAIL_FUNNEL = [
  { label: "Leads contacted", value: 609, pct: 100, note: "" },
  { label: "Opened", value: 363, pct: 60, note: "59.61% open rate" },
  { label: "Replied", value: 43, pct: 20, note: "11.85% reply rate" },
  { label: "Positive replies", value: 25, pct: 12, note: "58.14% of replies" },
];

const ENGINE_STATS = [
  { value: "2,120", label: "Emails sent across campaigns" },
  { value: "6", label: "Campaigns" },
  { value: "4", label: "Regions covered" },
  { value: "0.33%", label: "Bounce rate" },
];

export const ResultsCaseStudy = () => (
  <section id="results" className="py-20 sm:py-28 border-t border-[#F0EBF8]" data-testid="results-section">
    <div className="max-w-6xl mx-auto px-6">
      <SectionHeading
        eyebrow="Case study — real results"
        title="Real results and replies from live cold email + LinkedIn outreach."
        sub="Every number below comes straight from our campaign dashboards — anonymized, exactly as tracked."
      />

      <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
        {RESULT_STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08}>
            <div
              data-testid={`result-stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="rounded-3xl border border-[#E9E4F0] bg-[#EDEDF1] p-6 text-center"
            >
              <p className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-violet-700">{s.value}</p>
              <p className="mt-2 text-xs sm:text-sm font-medium text-[#6B5CA5]">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1} className="mt-6">
        <div className="rounded-3xl border border-[#E9E4F0] bg-[#EDEDF1] p-8 sm:p-10" data-testid="linkedin-outreach-results">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-violet-700">
            <Linkedin className="h-4 w-4" />
            LinkedIn outreach — real activity
          </p>
          <p className="mt-3 text-sm text-[#6B5CA5] max-w-2xl">
            From a live connection + follow-up sequence, tracked row by row in our outreach sheet.
            Names and companies stay private.
          </p>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {OUTREACH_STATS.map((s) => (
              <div
                key={s.label}
                data-testid={`outreach-stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-2xl border border-[#E9E4F0] bg-white p-5 text-center"
              >
                <p className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-violet-700">{s.value}</p>
                <p className="mt-1.5 text-xs font-medium text-[#6B5CA5]">{s.label}</p>
              </div>
            ))}
          </div>

          <p className="mt-8 text-xs font-semibold uppercase tracking-[0.2em] text-[#6B5CA5]">Where prospects landed</p>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {LEAD_STATUSES.map((s) => (
              <span
                key={s.label}
                data-testid={`lead-status-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold ${s.cls}`}
              >
                {s.label}
                <span className="opacity-70">× {s.count}</span>
              </span>
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.2} className="mt-6">
        <div className="rounded-3xl border border-[#E9E4F0] bg-[#EDEDF1] p-8 sm:p-10" data-testid="email-outreach-results">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-violet-700">
            <Mail className="h-4 w-4" />
            Cold email — real campaign results
          </p>
          <p className="mt-3 text-sm text-[#6B5CA5] max-w-2xl">
            From a live email campaign tracked in Smartlead — every number straight from the dashboard.
          </p>
          <div className="mt-8 space-y-4 max-w-2xl" data-testid="email-funnel">
            {EMAIL_FUNNEL.map((f) => (
              <div key={f.label} data-testid={`funnel-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-sm font-semibold text-[#3B0764]">{f.label}</span>
                  <span className="text-sm text-[#6B5CA5]">
                    <span className="font-display font-bold text-violet-700">{f.value}</span>
                    {f.note ? <span className="ml-2 text-xs">{f.note}</span> : null}
                  </span>
                </div>
                <div className="mt-1.5 h-2.5 rounded-full bg-white border border-[#E9E4F0] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-violet-600 transition-[width] duration-700"
                    style={{ width: `${Math.max(f.pct, 6)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <p className="mt-10 text-xs font-semibold uppercase tracking-[0.2em] text-violet-700">
            Email campaign setup &amp; personalized outreach
          </p>
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-2xl" data-testid="campaign-engine-results">
            {ENGINE_STATS.map((s) => (
              <div
                key={s.label}
                data-testid={`engine-stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-2xl border border-[#E9E4F0] bg-white p-4 text-center"
              >
                <p className="font-display text-xl sm:text-2xl font-extrabold tracking-tight text-violet-700">{s.value}</p>
                <p className="mt-1 text-[11px] font-medium text-[#6B5CA5] leading-snug">{s.label}</p>
              </div>
            ))}
          </div>
          <p className="mt-5 text-xs text-[#6B5CA5] max-w-2xl">
            Across all campaigns, run on dedicated warmed sending accounts — client names stay private.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
